import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { Box, Paragraph } from 'theme-ui';
import { Svg } from '@snippets';
import {
  useProductInventoryByHandle,
  useSettings,
} from '@backpackjs/storefront';

import { getEncodedId, isEncoded } from '@utils';

import { themed } from './Badge.theme';

export const Badge = themed(({ theme, handle, product, isPdp }) => {
  const settings = useSettings();
  const { customBadges, soldOutBadge, onSaleBadge } = {
    ...settings?.product?.badges,
  };
  const { inventory } = useProductInventoryByHandle({
    handle,
    withQuantity: true,
  });
  const { route } = useRouter();

  const variants = useMemo(() => {
    if (!product) return [];
    const _variants = [];
    product.variants?.find((variant) => {
      if (variant.product.handle === product.handle) {
        _variants.push(variant);
        return false;
      }
      return true;
    });
    return _variants;
  }, [product?.id]);

  const isSoldOut = useMemo(() => {
    if (!variants?.length || inventory?.variants?.length) return null;
    return variants.every((variant) => {
      const variantStorefrontId = isEncoded(inventory?.id)
        ? getEncodedId(variant.storefrontId)
        : variant.storefrontId;
      const variantInventory = inventory
        ? inventory?.variants?.[variantStorefrontId]
        : null;

      const variantSoldOut = variantInventory
        ? variantInventory.quantityAvailable <= 0 &&
          variant.inventoryPolicy !== 'CONTINUE' &&
          variantInventory.product?.handle !== 'brumate-e-gift-card'
        : false;

      return variantSoldOut;
    });
  }, [variants, inventory?.variants]);

  const selectedVariant = product?.variants?.[0];
  const price = selectedVariant?.priceV2?.amount;
  const compareAtPrice = selectedVariant?.compareAtPriceV2?.amount;

  const isOnSale = useMemo(
    () => parseFloat(price) < parseFloat(compareAtPrice),
    [price, compareAtPrice]
  );

  const isPersonalization = useMemo(() => {
    if (isPdp) return false;
    return (
      product?.tags?.find(
        (tag) => tag?.toLowerCase() === 'personalized-enabled'
      ) === 'personalized-enabled'
    );
  }, [product?.id, isPdp]);

  const badge = useMemo(() => {
    if (!product?.tags?.length || !customBadges?.length) return null;

    const shopifyTag = product?.tags?.find((tag) => tag.startsWith('badge::'));

    const match = customBadges.find((_badge) => _badge.tag === shopifyTag);

    if (match) return match;

    if (shopifyTag?.toLowerCase() === 'badge::new')
      return {
        text: 'New',
        color: '#000000',
        backgroundColor: 'transparent',
        tag: 'badge::New',
      };

    return match;
  }, [product?.id, customBadges]);

  const bersonalizeBadge = useMemo(() => {
    return (
      isPersonalization &&
      !isPdp && (
        <Paragraph
          data-comp={Badge.displayName}
          sx={{
            ...theme.personalize(isPdp),
            backgroundColor: 'transparent',
            marginLeft: '8px',
          }}
        >
          <Svg
            alt="personalization icon"
            src="/svgs/noprecache/svg/pencil.svg"
            viewBox="0 0 19 19"
            width="19px"
            height="19px"
          />
          <span className="sr-only">Personalize</span>
        </Paragraph>
      )
    );
  }, [isPersonalization, isPdp]);

  const badgeContent = useMemo(() => {
    switch (true) {
      case isSoldOut: {
        return (
          <Paragraph
            data-comp={Badge.displayName}
            sx={{
              ...theme.badge(isPdp),
              color: soldOutBadge?.color || 'white',
              bg: soldOutBadge?.backgroundColor || '#5d5d5d',
            }}
          >
            {soldOutBadge?.text || 'Sold Out'}
          </Paragraph>
        );
      }
      case isOnSale: {
        return (
          <Paragraph
            data-comp={Badge.displayName}
            sx={{
              ...theme.badge(isPdp),
              color: onSaleBadge?.color || 'red',
              bg: onSaleBadge?.backgroundColor || 'transparent',
            }}
          >
            {onSaleBadge?.text || 'Sale'}
          </Paragraph>
        );
      }
      case !!badge:
        return (
          <Paragraph
            data-comp={Badge.displayName}
            sx={{
              ...theme.badge(isPdp),
              color: badge?.color || 'black',
              bg: badge?.backgroundColor || 'transparent',
            }}
          >
            {badge?.text}
          </Paragraph>
        );
      default:
        return null;
    }
  }, [isSoldOut, isOnSale, badge]);

  return (
    (!!badgeContent || !!bersonalizeBadge) && (
      <Box sx={{ ...theme.badgeWrapper(isPdp) }}>
        {badgeContent}
        {bersonalizeBadge}
      </Box>
    )
  );
});

Badge.displayName = 'Badge';
